// CommandPalette — Cmd+K 명령 팔레트 메인 컴포넌트 (U48 + U49)
//
// 책임:
//   - Radix Dialog (portal) 기반 모달 레이아웃
//   - 입력창 (prefix 힌트 포함)
//   - ↑↓ Enter Esc 키보드 처리
//   - CommandList에 모드·포커스 상태 전달
//   - 외부 open 상태를 useCommandPalette hook과 연결
//   - 명령 실행 시 useCmdkHistory.push 호출 (U49)
//
// 사용 위치: App.tsx의 RequireAuth 영역 안에서 전역 마운트.
// Cmd+K 단축키는 useGlobalShortcuts에 통합.

import { useRef, useEffect, useCallback } from 'react';
import { Root, Portal, Overlay, Content, Title } from '@radix-ui/react-dialog';
import { Search, X } from 'lucide-react';
import { CommandList } from './CommandList';
import { useCommandPalette } from '../../hooks/useCommandPalette';
import { useCmdkHistory } from '../../hooks/useCmdkHistory';
import type { UseCommandPaletteReturn } from '../../hooks/useCommandPalette';

// ─── Props ────────────────────────────────────────────────────

export interface CommandPaletteProps {
  /**
   * 외부 hook 인스턴스.
   * App.tsx에서 useCommandPalette()를 호출하고 props로 전달.
   * 이렇게 하면 팔레트 외부(Cmd+K 단축키)에서도 같은 상태를 제어할 수 있다.
   */
  palette: UseCommandPaletteReturn;
}

// ─── prefix 힌트 뱃지 컴포넌트 ──────────────────────────────

interface ModeBadgeProps {
  mode: UseCommandPaletteReturn['mode'];
}

/** 입력창 좌측에 현재 모드를 표시하는 작은 뱃지 */
function ModeBadge({ mode }: ModeBadgeProps) {
  const labels: Record<typeof mode, string | null> = {
    command: '명령',
    search: '검색',
    navigate: '이동',
    mixed: null,
  };
  const label = labels[mode];
  if (!label) return null;
  return <span className={`cmd-mode-badge cmd-mode-badge--${mode}`}>{label}</span>;
}

// ─── placeholder 텍스트 ──────────────────────────────────────

function getPlaceholder(mode: UseCommandPaletteReturn['mode']): string {
  switch (mode) {
    case 'command': return '명령어 검색…';
    case 'search':  return '항목 검색…';
    case 'navigate': return '그룹 또는 태그 이동…';
    default:        return '명령, 검색, 이동… (> / @)';
  }
}

// ─── CommandPalette 구현 ──────────────────────────────────────

/**
 * 명령 팔레트 UI 컴포넌트.
 * Radix Dialog Portal로 DOM 최상위에 렌더된다.
 */
export function CommandPalette({ palette }: CommandPaletteProps) {
  const {
    open,
    closePalette,
    query,
    setQuery,
    searchTerm,
    debouncedSearchTerm,
    mode,
    focusIndex,
    setFocusIndex,
    setResultCount,
    resultCount,
    movePrev,
    moveNext,
  } = palette;

  // 최근 사용 기록 훅 (U49)
  const history = useCmdkHistory();

  const inputRef = useRef<HTMLInputElement>(null);

  // 팔레트 열릴 때 입력창 자동 포커스
  useEffect(() => {
    if (open) {
      // 다음 tick에 포커스 — Radix Dialog 애니메이션 완료 후
      const timer = setTimeout(() => {
        inputRef.current?.focus();
      }, 10);
      return () => clearTimeout(timer);
    }
  }, [open]);

  // 현재 포커스된 항목 클릭 실행
  // Enter 키는 keydown 핸들러에서 처리하지 않고
  // 포커스된 ResultRow의 onAction을 직접 호출하는 방식으로 구현.
  // 이를 위해 포커스된 행에 data-focused 속성을 달고 click() 호출.
  const executeCurrentFocused = useCallback(() => {
    if (focusIndex < 0) return;
    const focused = document.querySelector<HTMLDivElement>(
      `[data-testid="cmd-row-${focusIndex}"]`,
    );
    // data-testid 기반 접근 대신 aria-selected 활용
    const selectedRow = document.querySelector<HTMLDivElement>(
      '[role="option"][aria-selected="true"]',
    );
    selectedRow?.click();
  }, [focusIndex]);

  // 키보드 이벤트 핸들러
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      switch (e.key) {
        case 'ArrowUp':
          e.preventDefault();
          movePrev();
          break;

        case 'ArrowDown':
          e.preventDefault();
          moveNext();
          break;

        case 'Enter':
          e.preventDefault();
          executeCurrentFocused();
          break;

        case 'Escape':
          // Radix Dialog가 Esc를 감지하지만,
          // 입력 영역에 포커스가 있을 때도 확실히 닫히도록 직접 처리
          closePalette();
          break;

        default:
          break;
      }
    },
    [movePrev, moveNext, executeCurrentFocused, closePalette],
  );

  return (
    <Root open={open} onOpenChange={(v) => { if (!v) closePalette(); }}>
      <Portal>
        {/* 반투명 오버레이 — 클릭 시 닫기 */}
        <Overlay className="cmd-overlay" onClick={closePalette} />

        {/* 팔레트 패널 */}
        <Content
          className="cmd-palette"
          aria-label="명령 팔레트"
          // Radix의 기본 Esc 핸들러가 onOpenChange(false)를 호출하므로 중복 방지
          onEscapeKeyDown={closePalette}
          // 포커스트랩 — input에 자동 포커스되므로 Radix 기본 동작 유지
        >
          {/* 접근성: Radix Dialog는 Title이 필수. 시각적으로 숨김 처리. */}
          <Title
            style={{
              position: 'absolute',
              width: '1px',
              height: '1px',
              padding: 0,
              margin: '-1px',
              overflow: 'hidden',
              clip: 'rect(0,0,0,0)',
              border: 0,
            }}
          >
            명령 팔레트
          </Title>

          {/* ── 입력 영역 ──────────────────────────────── */}
          <div className="cmd-palette__input-wrap">
            <Search className="cmd-palette__search-icon" size={18} aria-hidden="true" />

            <ModeBadge mode={mode} />

            <input
              ref={inputRef}
              type="text"
              role="combobox"
              aria-expanded={open}
              aria-haspopup="listbox"
              aria-autocomplete="list"
              aria-controls="cmd-palette-list"
              className="cmd-palette__input"
              placeholder={getPlaceholder(mode)}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              autoComplete="off"
              spellCheck={false}
              data-testid="cmd-palette-input"
            />

            {/* 쿼리 지우기 버튼 */}
            {query.length > 0 && (
              <button
                type="button"
                className="cmd-palette__clear"
                onClick={() => {
                  setQuery('');
                  inputRef.current?.focus();
                }}
                aria-label="검색어 지우기"
                tabIndex={-1}
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* ── 구분선 ─────────────────────────────────── */}
          <div className="cmd-palette__divider" role="separator" />

          {/* ── 결과 리스트 ────────────────────────────── */}
          <div
            id="cmd-palette-list"
            className="cmd-palette__list-wrap"
          >
            <CommandList
              mode={mode}
              searchTerm={searchTerm}
              debouncedSearchTerm={debouncedSearchTerm}
              focusIndex={focusIndex}
              onFocusChange={setFocusIndex}
              onResultCountChange={setResultCount}
              onClose={closePalette}
              history={history}
            />
          </div>

          {/* ── 키보드 힌트 푸터 ────────────────────────── */}
          <div className="cmd-palette__footer" role="presentation">
            <span className="cmd-palette__hint">
              <kbd>↑↓</kbd> 이동
            </span>
            <span className="cmd-palette__hint">
              <kbd>Enter</kbd> 실행
            </span>
            <span className="cmd-palette__hint">
              <kbd>Esc</kbd> 닫기
            </span>
            <span className="cmd-palette__hint cmd-palette__hint--modes">
              <kbd>&gt;</kbd> 명령 &nbsp;
              <kbd>/</kbd> 검색 &nbsp;
              <kbd>@</kbd> 이동
            </span>
          </div>
        </Content>
      </Portal>
    </Root>
  );
}

// ─── 글로벌 마운트 컴포넌트 ──────────────────────────────────

/**
 * App.tsx에서 RequireAuth 영역 안에 마운트하는 래퍼.
 * 내부에서 useCommandPalette()를 호출하고 Cmd+K 단축키를 등록.
 * 팔레트 상태가 이 컴포넌트 안에 격리된다.
 */
export function CommandPaletteMount() {
  const palette = useCommandPalette();

  // Cmd+K (Mac) / Ctrl+K (Win) 글로벌 단축키 등록
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.userAgent);
      const trigger = isMac ? e.metaKey && e.key === 'k' : e.ctrlKey && e.key === 'k';

      if (trigger) {
        e.preventDefault();
        if (palette.open) {
          palette.closePalette();
        } else {
          palette.openPalette();
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [palette]);

  return <CommandPalette palette={palette} />;
}
